import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { donationService } from '../../services';
import { formatCurrency } from '../../utils/formatters';
import PageHeader from '../../components/common/PageHeader';

const MyDonations = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [donations, setDonations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [voucherPreview, setVoucherPreview] = useState(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const limit = 10;

  useEffect(() => {
    fetchDonations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const fetchDonations = async () => {
    try {
      setLoading(true);
      setError('');
      const params = {
        limit,
        offset: (currentPage - 1) * limit,
        sortBy: 'createdAt',
        order: 'DESC',
      };

      const response = await donationService.getMyDonations(params);
      const donationsList = response?.data?.items || response?.items || [];
      const total = response?.data?.totalCount || response?.totalCount || 0;

      setDonations(donationsList);
      setTotalCount(total);
    } catch (err) {
      console.error('Error fetching donations:', err);
      setError('Failed to load donations. Please try again.');
    } finally {
      setLoading(false);
    }
  };


  const getTargetTypeBadge = (type) => {
    const styles = {
      CANDIDATE: 'bg-blue-100 text-blue-800',
      PARTY: 'bg-purple-100 text-purple-800',
    };

    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${styles[type] || 'bg-gray-100 text-gray-800'}`}>
        {type === 'CANDIDATE' ? t('donations.gurukul') : t('donations.organization')}
      </span>
    );
  };


  const handlePrintReceipt = () => {
    window.print();
  };

  const getStatusBadge = (status) => {
    const styles = {
      SUCCESS: 'bg-green-100 text-green-800',
      PENDING: 'bg-yellow-100 text-yellow-800',
      FAILED: 'bg-red-100 text-red-800',
      CANCELLED: 'bg-gray-100 text-gray-800',
    };

    let label = status;
    if (status === 'PENDING') label = 'Under review';
    if (status === 'SUCCESS') label = 'Payment successful';

    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
        {label}
      </span>
    );
  };

  const totalPages = Math.ceil(totalCount / limit);

  if (loading && donations.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-primary-50">
      <PageHeader
        title={t('myDonations.title')}
        subtitle={t('myDonations.subtitle')}
      />
      <div className="max-w-7xl mx-auto px-4 pb-12">

        {error && !showReceiptModal && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
            {error}
          </div>
        )}



        {/* Donations Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-100 border-b-2 border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('common.date')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('donations.targetType')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('donations.gurukul')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('common.amount')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('donate.payment.title')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('common.status')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('myDonations.receipt')}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('admin.quickActions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {donations.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center">
                        <svg className="w-16 h-16 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <circle cx="12" cy="12" r="9" strokeWidth="2" />
                          <text x="12" y="15.5" textAnchor="middle" fontSize="10" fontWeight="bold" fill="currentColor" stroke="none">रु</text>
                        </svg>
                        <p className="text-gray-600 mb-4">{t('myDonations.noDonations')}</p>
                        <Link
                          to="/gurukuls"
                          className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition"
                        >
                          {t('myDonations.browseGurukuls')}
                        </Link>
                      </div>
                    </td>
                  </tr>
                ) : (
                  donations.map((donation) => (
                    <tr key={donation.id} className="hover:bg-primary-50/50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {new Date(donation.createdAt).toLocaleDateString()}
                        </div>
                        <div className="text-xs text-gray-500">
                          {new Date(donation.createdAt).toLocaleTimeString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getTargetTypeBadge(donation.targetType)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {donation.candidate?.name || t('donations.organization')}
                        </div>
                        <div className="text-xs text-gray-500">
                          {donation.candidate?.nagarpalikaGaupalika || donation.candidate?.constituency || ''}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-gray-900">
                          {formatCurrency(donation.amount, lang)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {donation.paymentMethod || 'VOUCHER'}
                        </div>
                        {donation.paymentMethod === 'VOUCHER' && (donation.voucherImageUrl || donation.voucherImage) && (
                          <button
                            type="button"
                            onClick={() => setVoucherPreview(donation.voucherImageUrl || donation.voucherImage)}
                            className="text-primary-600 hover:text-primary-900 text-xs mt-1 flex items-center font-medium"
                          >
                            <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                            {t('myDonations.viewVoucher')}
                          </button>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(donation.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {donation.receiptNumber ? (
                          <div className="flex flex-col">
                            <span className="text-xs font-mono text-gray-500 bg-primary-50 px-1 py-0.5 rounded border border-primary-100">
                              {donation.receiptNumber}
                            </span>
                            {donation.receiptUrlUrl && (
                              <a
                                href={donation.receiptUrlUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary-600 hover:text-primary-900 text-xs mt-1.5 flex items-center font-medium"
                              >
                                <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                </svg>
                                {t('actions.download')}
                              </a>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400 text-xs">{t('common.notProvided')}</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        {donation.status === 'SUCCESS' && donation.receiptUrlUrl && (
                          <a
                            href={donation.receiptUrlUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary-600 hover:text-primary-900 transition"
                          >
                            {t('myDonations.viewReceipt')}
                          </a>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-primary-50 disabled:opacity-50"
                >
                  {t('pagination.previous')}
                </button>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-primary-50 disabled:opacity-50"
                >
                  {t('pagination.next')}
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    {t('pagination.showing')} <span className="font-medium">{(currentPage - 1) * limit + 1}</span> {t('pagination.to')}{' '}
                    <span className="font-medium">{Math.min(currentPage * limit, totalCount)}</span> {t('pagination.of')}{' '}
                    <span className="font-medium">{totalCount}</span> {t('pagination.results')}
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    {[...Array(Math.min(totalPages, 5))].map((_, idx) => {
                      const pageNum = idx + 1;
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === pageNum
                            ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                            }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Receipt Modal */}
      {showReceiptModal && selectedReceipt && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4 print:hidden">
              <h3 className="text-2xl font-bold text-gray-900">{t('myDonations.receiptModal.title')}</h3>
              <div className="flex gap-2">
                <button
                  onClick={handlePrintReceipt}
                  className="inline-flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                  </svg>
                  Print
                </button>
                <button
                  onClick={() => {
                    setShowReceiptModal(false);
                    setSelectedReceipt(null);
                  }}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="border-2 border-gray-200 rounded-lg p-8">
              {/* Receipt Header */}
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-primary-600 mb-2">{t('site.fullName')}</h2>
                <p className="text-gray-600">{t('myDonations.receiptModal.title')}</p>
              </div>

              {/* Receipt Details */}
              <div className="grid grid-cols-2 gap-6 mb-6">
                <div>
                  <p className="text-sm text-gray-500 mb-1">{t('myDonations.receiptModal.receiptNo')}</p>
                  <p className="font-semibold">{selectedReceipt.receiptNumber || selectedReceipt.id || t('common.notProvided')}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">{t('common.date')}</p>
                  <p className="font-semibold">
                    {selectedReceipt.createdAt
                      ? new Date(selectedReceipt.createdAt).toLocaleDateString()
                      : t('common.notProvided')}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">{t('myDonations.receiptModal.donor')}</p>
                  <p className="font-semibold">{selectedReceipt.donor?.fullName || t('donations.anonymous')}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">{t('myDonations.receiptModal.method')}</p>
                  <p className="font-semibold">{selectedReceipt.paymentMethod || 'VOUCHER'}</p>
                </div>
              </div>

              {/* Donation Details */}
              <div className="border-t border-gray-200 pt-6 mb-6">
                <h4 className="font-semibold mb-4">{t('reports.details')}</h4>
                <div className="bg-primary-50/50 p-4 rounded-lg border border-primary-100">
                  <div className="flex justify-between mb-2">
                    <span className="text-gray-600">{t('myDonations.receiptModal.target')}:</span>
                    <span className="font-semibold">{selectedReceipt.candidate?.name || selectedReceipt.party?.name || t('donations.organization')}</span>
                  </div>
                  <div className="flex justify-between mb-2">
                    <span className="text-gray-600">{t('myDonations.receiptModal.constituency')}:</span>
                    <span className="font-semibold">{selectedReceipt.candidate?.nagarpalikaGaupalika || selectedReceipt.candidate?.constituency || t('common.notProvided')}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold text-primary-600 pt-2 border-t border-gray-300 mt-2">
                    <span>{t('myDonations.receiptModal.amount')}:</span>
                    <span>{formatCurrency(selectedReceipt.amount || 0, lang)}</span>
                  </div>
                </div>
              </div>

              {/* Transaction Info */}
              {selectedReceipt.transactionId && (
                <div className="border-t border-gray-200 pt-6 mb-6">
                  <p className="text-sm text-gray-500 mb-1">{t('myDonations.receiptModal.transactionId')}</p>
                  <p className="font-mono text-sm">{selectedReceipt.transactionId}</p>
                </div>
              )}

              {/* Footer */}
              <div className="border-t border-gray-200 pt-6 text-center text-sm text-gray-500">
                <p>{t('myDonations.receiptModal.footer')}</p>
                <p className="mt-2">{t('myDonations.receiptModal.footerSubtitle')}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Voucher Preview Modal */}
      {voucherPreview && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-80 flex items-center justify-center p-4">
          <button
            type="button"
            className="absolute top-4 right-4 text-white hover:text-gray-200"
            onClick={() => setVoucherPreview(null)}
          >
            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <div className="max-w-3xl max-h-[90vh] bg-white rounded-xl overflow-hidden shadow-2xl">
            <img
              src={voucherPreview}
              alt="Voucher"
              className="w-full h-full object-contain bg-black"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default MyDonations;
